// Génère une carte d'étage de type "couloir" :
//   - 1 sanctuaire (passif) au départ
//   - 3 rangées de 3 choix (combat/élite/coffre/boutique/sanctuaire)
//   - 1 boss à la fin
// Chaque nœud d'une rangée intermédiaire mène à 1-2 nœuds de la suivante.

const CHOICE_POOL = ["combat", "combat", "combat", "elite", "chest", "shop", "sanctuary"];
const CHOICE_ROWS = 6;          // 6 rangées de choix (run plus longue)
const NODES_PER_CHOICE = 3;     // 3 colonnes (choix) par rangée

// Mulberry32 — petit PRNG déterministe seedé par un entier 32-bit
const makeRng = (seed) => {
  let a = (seed | 0) || 1;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pickSeeded = (arr, rng) => arr[Math.floor(rng() * arr.length)];

// Pondère le pool selon la rangée (rangées finales => plus d'élite/boutique)
const poolForChoiceRow = (choiceRowIdx, floor = 99) => {
  let pool = [...CHOICE_POOL];
  // Les Élites n'apparaissent qu'à partir de l'étage 5
  if (floor < 5) pool = pool.filter((t) => t !== "elite");
  if (choiceRowIdx === 0) {
    // Début doux : pas d'élite, plus de coffre/boutique
    return pool.filter((t) => t !== "elite").concat(["chest", "shop"]);
  }
  if (choiceRowIdx === CHOICE_ROWS - 1) {
    // Pré-boss : plus d'élite (si étage ≥ 5), plus de sanctuaire
    return pool.concat(floor >= 5 ? ["elite", "sanctuary"] : ["sanctuary"]);
  }
  return pool;
};

export const generateFloorMap = (floor = 1, seed = null) => {
  const rng = seed != null
    ? makeRng((seed * 1000003) ^ floor)
    : Math.random;

  const map = [];
  // Rangée 0 : sanctuaire de passif (point de départ)
  map.push([{ id: `${floor}-0-0`, row: 0, col: 0, totalCols: 1, type: "sanctuary", visited: false, completed: false }]);

  // Rangées 1..CHOICE_ROWS : 3 choix chacune
  for (let r = 1; r <= CHOICE_ROWS; r++) {
    const nodes = [];
    for (let i = 0; i < NODES_PER_CHOICE; i++) {
      nodes.push({
        id: `${floor}-${r}-${i}`,
        row: r,
        col: i,
        totalCols: NODES_PER_CHOICE,
        type: pickSeeded(poolForChoiceRow(r - 1, floor), rng),
        visited: false,
        completed: false,
      });
    }
    map.push(nodes);
  }

  // Rangée finale : boss
  const finalRow = CHOICE_ROWS + 1;
  map.push([{ id: `${floor}-${finalRow}-0`, row: finalRow, col: 0, totalCols: 1, type: "boss", visited: false, completed: false }]);

  // Connexions
  // - Le sanctuaire de départ connecte aux 3 nœuds de la première rangée de choix
  // - Chaque nœud d'une rangée intermédiaire connecte à 1-2 nœuds adjacents de la suivante
  // - Tous les nœuds de la dernière rangée de choix mènent au boss
  for (let r = 0; r < map.length - 1; r++) {
    const currentRow = map[r];
    const nextRow = map[r + 1];

    if (currentRow.length === 1) {
      // Sanctuaire de départ OU boss (jamais) — connecte au pré-boss
      currentRow[0].connections = nextRow.map((n) => n.id);
      continue;
    }

    if (nextRow.length === 1) {
      // Avant-boss : chaque choix mène au boss unique
      for (const node of currentRow) {
        node.connections = [nextRow[0].id];
      }
      continue;
    }

    // Rangées de choix vers rangée de choix : chaque nœud connecte à sa colonne + parfois une adjacente
    for (const node of currentRow) {
      const set = new Set([node.col]);
      if (rng() < 0.5) {
        const offset = rng() < 0.5 ? -1 : 1;
        const second = Math.max(0, Math.min(nextRow.length - 1, node.col + offset));
        set.add(second);
      }
      node.connections = Array.from(set).map((idx) => nextRow[idx].id);
    }

    // Garantit qu'au moins un nœud connecte à chaque colonne suivante (pas d'orphelin)
    const reached = new Set();
    for (const node of currentRow) for (const cid of node.connections) reached.add(cid);
    for (let c = 0; c < nextRow.length; c++) {
      if (!reached.has(nextRow[c].id)) {
        // Trouve le nœud courant le plus proche et lui ajoute la connexion
        const nearest = currentRow.reduce((best, n) =>
          Math.abs(n.col - c) < Math.abs(best.col - c) ? n : best
        );
        nearest.connections = Array.from(new Set([...(nearest.connections || []), nextRow[c].id]));
      }
    }
  }

  return map;
};

// Retourne les IDs des nœuds disponibles depuis un nœud donné
export const getNextNodes = (map, currentNodeId) => {
  if (!map || map.length === 0) return [];
  if (!currentNodeId) {
    return (map[0] || []).map((n) => n.id);
  }
  for (const row of map) {
    for (const node of row) {
      if (node.id === currentNodeId) {
        return node.connections || [];
      }
    }
  }
  return [];
};

export const findNode = (map, nodeId) => {
  for (const row of map) {
    for (const node of row) {
      if (node.id === nodeId) return node;
    }
  }
  return null;
};
